/**
 * Business Login for Instagram — launcher do OAuth de MENSAGERIA do Instagram
 * (WP-6 do programa Instagram Direct).
 *
 * **MEDIDO 2026-08-31 (URL incorporada real do painel):** o app "JanusOS AI-IG"
 * usa **Business Login for Instagram** (redirect direto para
 * `instagram.com/oauth/authorize`), NÃO o Facebook Login for Business (popup
 * `FB.login` + `config_id`). Consequências:
 *
 * - NÃO há `config_id` — o authorize leva `client_id` (o App ID do Instagram),
 *   `redirect_uri`, `response_type=code` e `scope` (os `instagram_business_*`).
 * - A troca do `code` é no `api.instagram.com` (backend, janus-whatsapp), não
 *   no graph do Facebook. A conta IG É a identidade do login — sem descoberta
 *   Página→IG.
 *
 * Estratégia de UI: um **popup** para o authorize do Instagram. O redirect
 * volta para o PRÓPRIO app (`redirect_uri = https://app.janusos.ai`) DENTRO do
 * popup; como é a MESMA origem do opener, o bootstrap `instagramOAuthPopup`
 * (rodado cedo no boot) lê o `?code` da própria URL, faz `postMessage({code})`
 * ao opener e fecha. Assim `launchInstagramLogin()` continua resolvendo com o
 * `code` via Promise — o card não muda. (Preferido ao redirect full-page, que
 * perderia o estado da tela e exigiria um catcher global no router.)
 *
 * NADA aqui é segredo: `client_id` (App ID) e `redirect_uri` são
 * identificadores PÚBLICOS. Com o App ID ausente o card fica desabilitado —
 * dark até estar provisionado (`VITE_META_IG_APP_ID`).
 */
import { getMetaAppId } from './fbEmbeddedSignup';

export interface InstagramLoginResult {
  code: string;
}

/** Marcador do `state` para desambiguar o retorno do OAuth do Instagram de
 * qualquer outro `?code=` que caia na origem do app (ex.: LinkedIn). */
export const IG_OAUTH_STATE = 'janus_ig_connect';
const IG_AUTHORIZE_BASE = 'https://www.instagram.com/oauth/authorize';
/** Escopos EXATOS do app JanusOS AI-IG (passo 1 do painel / URL incorporada). */
const IG_SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_messages',
  'instagram_business_manage_comments',
  'instagram_business_content_publish',
  'instagram_business_manage_insights',
].join(',');
/** Mensagem postada do popup para o opener (canal best-effort). */
const IG_POPUP_MESSAGE = 'JANUS_IG_OAUTH';
/**
 * Chave de handoff popup→app via localStorage. É o canal PRINCIPAL: sob
 * `Cross-Origin-Opener-Policy: same-origin` (que o app serve), navegar o popup
 * para o instagram.com e voltar **corta o `window.opener`** — então
 * `postMessage(opener)` não tem para quem falar e o popup não conseguia se
 * anunciar. O `localStorage` é por-ORIGEM e o evento `storage` cruza janelas de
 * mesma origem independente de opener/COOP, então o popup escreve o resultado
 * aqui e o app o lê pelo evento `storage` (com polling de reforço). Medido
 * 2026-08-31: era exatamente o "volta pro app e não fecha". */
const IG_RESULT_KEY = 'janus_ig_oauth_result';
/** Teto de espera do login (autorizar na Meta leva tempo — login + 2FA + a
 * revisão de permissões). Só serve de desistência quando NENHUM canal trouxe o
 * resultado; o sucesso resolve na hora. NÃO usamos `popup.closed` para desistir
 * porque o COOP o torna não-confiável (ver `launchInstagramLogin`). */
const IG_CONNECT_GIVE_UP_MS = 3 * 60 * 1000;

function readEnv(value: string | undefined): string | undefined {
  const trimmed = value ? String(value).trim() : '';
  return trimmed ? trimmed : undefined;
}

/** App id PRÓPRIO do Instagram (`VITE_META_IG_APP_ID` = client_id), com
 * fallback ao App do WhatsApp (`VITE_META_APP_ID`) para quem unificar. */
export function getInstagramAppId(): string | undefined {
  return readEnv(import.meta.env.VITE_META_IG_APP_ID) ?? getMetaAppId();
}

/** redirect_uri público — DEVE bater byte a byte entre o authorize e a troca do
 * code (a Meta compara os dois). Domínio-RAIZ leva **barra final**
 * (`https://app.janusos.ai/`): o redirect HTTP real de uma origem nua ganha o
 * `/` de path, e a Meta grava essa forma canônica; mandar sem a barra na troca
 * dava `Meta 400: Error validating verification code. …redirect_uri identical…`
 * (medido em produção 2026-08-31). A MESMA função alimenta authorize e exchange,
 * então os dois seguem idênticos. Configurável para dev/staging. */
export function getInstagramRedirectUri(): string {
  return readEnv(import.meta.env.VITE_META_IG_REDIRECT_URI) ?? 'https://app.janusos.ai/';
}

/** True quando o App ID do Instagram está configurado (o redirect tem default). */
export function isInstagramLoginConfigured(): boolean {
  return Boolean(getInstagramAppId());
}

function buildAuthorizeUrl(): string {
  const params = new URLSearchParams({
    client_id: getInstagramAppId() as string,
    redirect_uri: getInstagramRedirectUri(),
    response_type: 'code',
    scope: IG_SCOPES,
    state: IG_OAUTH_STATE,
  });
  return `${IG_AUTHORIZE_BASE}?${params.toString()}`;
}

/**
 * Abre o popup do authorize do Instagram e resolve com o `code`. Rejeita com a
 * frase de cancelamento (spec §1.6) quando o usuário nega/fecha, ou com erro
 * de bloqueio de popup.
 */
export async function launchInstagramLogin(): Promise<InstagramLoginResult> {
  const appId = getInstagramAppId();
  if (!appId) {
    throw new Error(
      'A conexão do Instagram ainda não está configurada neste ambiente ' +
        '(VITE_META_IG_APP_ID).',
    );
  }

  // Limpa qualquer resultado velho ANTES de abrir — um handoff anterior que
  // não foi consumido não pode ser lido como o resultado desta tentativa.
  try {
    window.localStorage.removeItem(IG_RESULT_KEY);
  } catch {
    /* localStorage indisponível: cai no canal postMessage/observação de fecho */
  }

  return new Promise<InstagramLoginResult>((resolve, reject) => {
    const width = 600;
    const height = 720;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const popup = window.open(
      buildAuthorizeUrl(),
      'janus_ig_oauth',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
    if (!popup) {
      reject(
        new Error(
          'Não foi possível abrir a janela de conexão do Instagram. ' +
            'Libere popups para este site e tente novamente.',
        ),
      );
      return;
    }

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      window.clearInterval(pollTimer);
      window.clearInterval(closedWatch);
      window.clearTimeout(giveUpTimer);
    };
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    // Consome um resultado {code?, error?} vindo por QUALQUER canal e encerra.
    const consume = (data: { code?: string; error?: string } | undefined) => {
      if (!data) return;
      try {
        popup.close();
      } catch {
        /* noop */
      }
      if (data.code) {
        finish(() => resolve({ code: data.code as string }));
      } else {
        finish(() =>
          reject(new Error('Conexão não concluída — você cancelou a autorização na Meta.')),
        );
      }
    };

    // Lê e LIMPA o resultado do localStorage (o canal principal sob COOP).
    const drainStorage = (): boolean => {
      try {
        const raw = window.localStorage.getItem(IG_RESULT_KEY);
        if (!raw) return false;
        window.localStorage.removeItem(IG_RESULT_KEY);
        consume(JSON.parse(raw) as { code?: string; error?: string });
        return true;
      } catch {
        return false;
      }
    };

    // Canal 1 (principal): evento `storage` — dispara nas OUTRAS janelas de
    // mesma origem quando o popup grava o resultado. Sobrevive ao COOP.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== IG_RESULT_KEY || !event.newValue) return;
      drainStorage();
    };
    window.addEventListener('storage', onStorage);

    // Canal 1b (reforço): alguns navegadores não entregam o evento `storage`
    // de forma confiável entre popup e opener — poll curto do mesmo valor.
    const pollTimer = window.setInterval(drainStorage, 500);

    // Canal 2 (best-effort): postMessage, para o caso de o opener sobreviver
    // (sem COOP, ou mesmo-origem sem navegação cross-origin).
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; code?: string; error?: string } | undefined;
      if (!data || data.type !== IG_POPUP_MESSAGE) return;
      consume(data);
    };
    window.addEventListener('message', onMessage);

    // `popup.closed` é APENAS um acelerador de leitura, NUNCA motivo de recusa.
    // Sob COOP `same-origin`, depois que o popup navega para o instagram.com o
    // opener perde o handle e passa a LER `popup.closed === true` cedo demais,
    // mesmo com a janela ainda aberta (medido 2026-08-31: rejeitava a conexão
    // ANTES de o code chegar, e o popup fechava sozinho gravando um resultado
    // que ninguém mais ouvia). Como o handler do popup grava o localStorage
    // ANTES de fechar, um fecho REAL sempre deixa o resultado para o drain — daí
    // só drenamos aqui; a desistência fica por conta do timeout global.
    const closedWatch = window.setInterval(() => {
      if (popup.closed) drainStorage();
    }, 700);

    // Desistência por TEMPO (não por `popup.closed`): se em GIVE_UP_MS nada
    // chegou por nenhum canal, encerra com uma frase de repetir. Cobre o cancelo
    // real (o usuário fechou sem autorizar) sem o falso-positivo do COOP.
    const giveUpTimer = window.setTimeout(() => {
      finish(() =>
        reject(
          new Error('Conexão não concluída — tempo esgotado. Tente conectar novamente.'),
        ),
      );
    }, IG_CONNECT_GIVE_UP_MS);
  });
}

/**
 * Bootstrap do LADO DO POPUP — chamado o MAIS CEDO possível no boot do app
 * (antes do router renderizar). Se esta janela é o retorno do OAuth do
 * Instagram (`?code`/`?error` + o nosso `state`), grava o resultado no
 * `localStorage` (canal que o app lê), avisa o opener por postMessage
 * (best-effort) e fecha, SEM renderizar o app.
 *
 * **Detecção pelo `state`, NÃO pelo `window.opener`** (medido 2026-08-31): sob
 * `Cross-Origin-Opener-Policy: same-origin`, voltar de instagram.com zera o
 * `window.opener`; exigir opener aqui fazia o popup renderizar o app inteiro e
 * nunca se anunciar ("volta pro app e não fecha"). O `state=janus_ig_connect`
 * só existe neste retorno — o app nunca navega a si mesmo para essa URL (o
 * fluxo é sempre por popup) —, então é o discriminador seguro.
 *
 * Devolve `true` quando tratou (o chamador deve abortar o render), `false`
 * caso contrário. Seguro chamar sempre.
 */
export function handleInstagramOAuthPopup(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('state') !== IG_OAUTH_STATE) return false;
    const code = params.get('code') || undefined;
    const error = params.get('error') || params.get('error_message') || undefined;
    if (!code && !error) return false;

    // Canal principal: localStorage (atravessa o COOP que corta o opener).
    try {
      window.localStorage.setItem(
        IG_RESULT_KEY,
        JSON.stringify({ code, error, ts: Date.now() }),
      );
    } catch {
      /* sem localStorage: resta o postMessage abaixo, quando o opener sobrevive */
    }

    // Canal best-effort: se o opener sobreviveu (sem COOP), avisa direto.
    try {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage(
          { type: IG_POPUP_MESSAGE, code, error },
          window.location.origin,
        );
      }
    } catch {
      /* opener cortado pelo COOP — o localStorage já cobriu */
    }

    // Deixa uma frase caso o navegador recuse fechar a janela (o app já foi
    // avisado; a janela é descartável).
    try {
      document.documentElement.innerHTML =
        '<body style="font-family:system-ui,sans-serif;padding:24px;color:#334155">' +
        'Conta conectada. Você já pode fechar esta janela.</body>';
    } catch {
      /* noop */
    }
    window.close();
    return true;
  } catch {
    return false;
  }
}
